"use client";

import {
  HISTORY_FIELD_LABELS,
  type HistoryChange,
  type HistoryEvent,
} from "@/components/listings/financial-history-model";
import { Icon } from "@/components/ui/icon";
import { prettyNumber } from "@/lib/string-utils";
import { cn } from "@/lib/utils";

const SOURCE_LABELS: Record<string, string> = {
  MANUAL_EDIT: "Manual edit",
  PARTNER_SYNC: "Partner sync",
  IMPORT: "Import",
  SYSTEM: "System",
};

function formatHistoryChange(change: HistoryChange): string {
  if (change.kind === "removed") return "Removed";
  if (change.kind === "set") return `Set to $${prettyNumber(change.to)}`;

  const diff = change.to - change.from;
  const pct =
    change.from > 0 ? Math.round((diff / change.from) * 100) : null;
  const sign = diff > 0 ? "+" : "";
  const pctStr = pct != null ? ` (${sign}${pct}%)` : "";
  return `$${prettyNumber(change.from)} → $${prettyNumber(change.to)}${pctStr}`;
}

export function ListingFinancialTimeline({
  events,
}: {
  events: HistoryEvent[];
}) {
  if (events.length === 0) return null;

  return (
    <div className="relative flex flex-col gap-0">
      <div className="absolute bottom-3 left-[11px] top-3 w-px bg-border" />

      {events.map((event) => {
        const dateStr = event.createdAt.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

        return (
          <div key={event.id} className="relative flex gap-3 py-2.5 pl-0">
            <div className="relative z-10 mt-1 flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 border-background bg-muted">
              <Icon
                icon="BarLineChartSolid"
                className="size-3 text-muted-foreground"
                aria-hidden
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{dateStr}</span>
                <span aria-hidden="true">·</span>
                <span>{SOURCE_LABELS[event.source] ?? event.source}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                {event.changes.map((change) => (
                  <p key={change.field} className="text-sm">
                    <span className="font-medium text-foreground">
                      {HISTORY_FIELD_LABELS[change.field]}:
                    </span>{" "}
                    <span
                      className={cn(
                        "font-data",
                        change.kind === "change" &&
                          (change.increased
                            ? "text-amber-600"
                            : "text-emerald-600"),
                      )}
                    >
                      {formatHistoryChange(change)}
                    </span>
                  </p>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
